/**
 * Typed environment config. Access env vars only through this module so we
 * fail fast at startup instead of silently falling back at runtime.
 */

const requireServerEnv = (key: string): string => {
  const value = process.env[key];
  if (!value) {
    throw new Error(
      `Missing required server env var: ${key}. Check .env.local or Vercel project settings.`
    );
  }
  return value;
};

const optionalServerEnv = (key: string): string | undefined => {
  return process.env[key] || undefined;
};

export const siteConfig = {
  name: "Raíces RD",
  domain: "raicesrd.co",
  url: process.env.NEXT_PUBLIC_SITE_URL ?? "https://raicesrd.co",
  description:
    "Invierte en propiedades verificadas en República Dominicana desde los Estados Unidos. Pre-construcción, cierre 100% remoto, respaldo legal.",
  locale: "es_DO",
  contactEmail: "hola@raicesrd.co",
  social: {
    facebook: "https://www.facebook.com/profile.php?id=61567583484127",
    /** WhatsApp number in E.164 format (no spaces, leading +). */
    whatsappNumber: "+19789728282",
    /** Default pre-filled message when a visitor opens the chat link. */
    whatsappGreeting: "Hola, quiero saber más sobre Raíces RD.",
  },
} as const;

/** Builds a wa.me link with a pre-filled greeting. */
export function whatsappChatUrl(): string {
  const number = siteConfig.social.whatsappNumber.replace(/[^0-9]/g, "");
  const text = encodeURIComponent(siteConfig.social.whatsappGreeting);
  return `https://wa.me/${number}?text=${text}`;
}

export const serverEnv = {
  get databaseUrl() {
    return optionalServerEnv("DATABASE_URL");
  },
  get resendApiKey() {
    return optionalServerEnv("RESEND_API_KEY");
  },
  get resendFromEmail() {
    return optionalServerEnv("RESEND_FROM_EMAIL") ?? siteConfig.contactEmail;
  },
  /**
   * True when the persistence layer is fully configured. Services
   * (properties, projects, inquiries, agents, drafts) read this to
   * decide whether to use the Postgres repository or fall back to a
   * no-op. Keeping it optional lets the marketing site boot without
   * a database (e.g., during early local development).
   */
  get persistenceEnabled() {
    return Boolean(optionalServerEnv("DATABASE_URL"));
  },
  requireDatabaseUrl() {
    return requireServerEnv("DATABASE_URL");
  },
} as const;

/**
 * Supabase Auth — read by browser AND server. NEXT_PUBLIC_* prefix is
 * intentional: both keys are designed to be shipped to the browser
 * (RLS + JWT validation gate what they can actually do).
 *
 * Falls back to optional reads so the marketing site keeps booting
 * even when these aren't configured yet — auth-gated routes guard
 * against missing values explicitly.
 */
export const supabaseEnv = {
  get url() {
    return process.env.NEXT_PUBLIC_SUPABASE_URL;
  },
  get anonKey() {
    return process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  },
  get isConfigured() {
    return Boolean(
      process.env.NEXT_PUBLIC_SUPABASE_URL &&
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
    );
  },
  requireUrl(): string {
    if (!process.env.NEXT_PUBLIC_SUPABASE_URL) {
      throw new Error(
        "Missing NEXT_PUBLIC_SUPABASE_URL. Set it in .env.local and Vercel."
      );
    }
    return process.env.NEXT_PUBLIC_SUPABASE_URL;
  },
  requireAnonKey(): string {
    if (!process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY) {
      throw new Error(
        "Missing NEXT_PUBLIC_SUPABASE_ANON_KEY. Set it in .env.local and Vercel."
      );
    }
    return process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  },
  /**
   * Server-only secret key for admin operations (Auth Admin API,
   * Storage uploads, RLS-bypassing PostgREST writes).
   *
   * Prefers `SUPABASE_SECRET_KEY` (the new `sb_secret_*` format
   * Supabase introduced to replace the legacy JWT service_role key)
   * with a fallback to `SUPABASE_SERVICE_ROLE_KEY` during migration.
   * Once the new key is verified working and the legacy key is
   * revoked in Supabase, drop the fallback and the old env var.
   */
  requireSecretKey(): string {
    const next = process.env.SUPABASE_SECRET_KEY;
    const legacy = process.env.SUPABASE_SERVICE_ROLE_KEY;
    const key = next ?? legacy;
    if (!key) {
      throw new Error(
        "Missing SUPABASE_SECRET_KEY (or legacy SUPABASE_SERVICE_ROLE_KEY). Required for admin operations."
      );
    }
    return key;
  },
} as const;
